<template>
  <div
    ref="root"
    class="app-dropdown"
    :class="{ 'app-dropdown--open': isOpen, 'app-dropdown--disabled': disabled }"
  >
    <button
      type="button"
      class="app-dropdown__trigger"
      :disabled="disabled"
      @click="toggle"
      @keydown.down.prevent="open"
      @keydown.up.prevent="open"
      @keydown.enter.prevent="toggle"
      @keydown.esc.prevent="close"
    >
      <span :class="['app-dropdown__value', { 'app-dropdown__value--placeholder': !selectedLabel }]">
        {{ selectedLabel || placeholder || 'Select' }}
      </span>
      <span class="app-dropdown__caret" aria-hidden="true">▾</span>
    </button>

    <div
      v-if="isOpen"
      ref="panel"
      class="app-dropdown__panel"
      :class="{ 'app-dropdown__panel--up': openUp }"
      :style="panelStyle"
      role="listbox"
    >
      <div v-if="searchable" class="app-dropdown__search">
        <input
          ref="search"
          v-model="filter"
          type="text"
          class="app-dropdown__search-input"
          :placeholder="searchPlaceholder || 'Search…'"
          @keydown.esc.prevent="close"
          @keydown.down.prevent="moveActive(1)"
          @keydown.up.prevent="moveActive(-1)"
          @keydown.enter.prevent="selectActive"
        >
      </div>
      <ul class="app-dropdown__list">
        <li v-if="!filteredOptions.length" class="app-dropdown__empty">No results</li>
        <li
          v-for="(option, index) in filteredOptions"
          :key="`${getValue(option)}-${index}`"
          :class="['app-dropdown__option', { 'is-selected': isSelected(option), 'is-active': index === activeIndex }]"
          role="option"
          :aria-selected="isSelected(option)"
          @mouseenter="activeIndex = index"
          @mousedown.prevent="select(option)"
        >
          {{ getLabel(option) }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AppDropdown',
  props: {
    value: { type: [String, Number, Boolean], default: '' },
    options: { type: Array, default: () => [] },
    placeholder: { type: String, default: '' },
    searchable: { type: Boolean, default: false },
    searchPlaceholder: { type: String, default: '' },
    disabled: { type: Boolean, default: false }
  },
  data () {
    return {
      isOpen: false,
      filter: '',
      activeIndex: -1,
      openUp: false,
      panelStyle: null
    }
  },
  computed: {
    normalizedOptions () {
      return (this.options || []).map((option) => {
        if (option && typeof option === 'object') return option
        return { value: option, label: String(option) }
      })
    },
    filteredOptions () {
      const term = String(this.filter || '').trim().toLowerCase()
      if (!term) return this.normalizedOptions
      return this.normalizedOptions.filter((option) => String(this.getLabel(option) || '').toLowerCase().includes(term))
    },
    selectedLabel () {
      const found = this.normalizedOptions.find((option) => String(this.getValue(option)) === String(this.value))
      if (!found) return ''
      // shortLabel — if present — keeps the closed trigger compact even
      // when the panel rows carry a longer descriptive label (e.g. the
      // country selector shows "🇮🇳 +91" in the trigger and "🇮🇳 +91 ·
      // India" in the list).
      if (found && typeof found === 'object' && found.shortLabel != null) {
        return found.shortLabel
      }
      return this.getLabel(found)
    }
  },
  mounted () {
    document.addEventListener('mousedown', this.handleOutsideClick)
  },
  deactivated () {
    this.close()
  },
  beforeDestroy () {
    document.removeEventListener('mousedown', this.handleOutsideClick)
    this.detachPanel()
    this.unbindViewportListeners()
  },
  methods: {
    getValue (option) {
      return option && typeof option === 'object' ? option.value : option
    },
    getLabel (option) {
      if (option == null) return ''
      if (typeof option !== 'object') return String(option)
      return option.label != null ? option.label : option.value
    },
    isSelected (option) {
      return String(this.getValue(option)) === String(this.value)
    },
    toggle () {
      if (this.disabled) return
      if (this.isOpen) this.close()
      else this.open()
    },
    open () {
      if (this.disabled || this.isOpen) return
      this.isOpen = true
      this.filter = ''
      this.activeIndex = this.filteredOptions.findIndex((option) => this.isSelected(option))
      this.$nextTick(() => {
        this.attachPanel()
        this.updatePanelPosition()
        if (this.searchable && this.$refs.search) this.$refs.search.focus()
      })
      this.bindViewportListeners()
    },
    close () {
      if (!this.isOpen) return
      this.isOpen = false
      this.filter = ''
      this.activeIndex = -1
      this.unbindViewportListeners()
      this.detachPanel()
    },
    bindViewportListeners () {
      window.addEventListener('scroll', this.updatePanelPosition, true)
      window.addEventListener('resize', this.updatePanelPosition)
    },
    unbindViewportListeners () {
      window.removeEventListener('scroll', this.updatePanelPosition, true)
      window.removeEventListener('resize', this.updatePanelPosition)
    },
    attachPanel () {
      const panel = this.$refs.panel
      if (panel && panel.parentNode !== document.body) {
        document.body.appendChild(panel)
      }
    },
    detachPanel () {
      const panel = this.$refs.panel
      if (panel && panel.parentNode === document.body) {
        document.body.removeChild(panel)
      }
    },
    updatePanelPosition () {
      const trigger = this.$refs.root
      const panel = this.$refs.panel
      if (!trigger || !panel) return
      const rect = trigger.getBoundingClientRect()
      const margin = 4
      const viewportHeight = window.innerHeight
      const viewportWidth = window.innerWidth
      const desiredHeight = panel.offsetHeight || 240
      const spaceBelow = viewportHeight - rect.bottom - margin
      const spaceAbove = rect.top - margin
      const flipUp = spaceBelow < Math.min(desiredHeight, 200) && spaceAbove > spaceBelow
      this.openUp = flipUp
      const maxHeight = Math.max(140, Math.floor(flipUp ? spaceAbove : spaceBelow))
      const top = flipUp
        ? Math.max(margin, rect.top - desiredHeight - margin)
        : rect.bottom + margin
      const width = rect.width
      const left = Math.min(Math.max(margin, rect.left), Math.max(margin, viewportWidth - width - margin))
      this.panelStyle = {
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        right: 'auto',
        width: `${width}px`,
        minWidth: `${width}px`,
        maxHeight: `${maxHeight}px`,
        zIndex: 1080
      }
    },
    select (option) {
      const value = this.getValue(option)
      this.$emit('update:value', value)
      this.$emit('input', value)
      this.close()
    },
    moveActive (delta) {
      const len = this.filteredOptions.length
      if (!len) return
      this.activeIndex = ((this.activeIndex + delta) % len + len) % len
    },
    selectActive () {
      const option = this.filteredOptions[this.activeIndex]
      if (option) this.select(option)
    },
    handleOutsideClick (event) {
      const root = this.$refs.root
      const panel = this.$refs.panel
      if (root && root.contains(event.target)) return
      if (panel && panel.contains(event.target)) return
      this.close()
    }
  }
}
</script>

<style scoped>
.app-dropdown {
  position: relative;
  width: 100%;
}

.app-dropdown__trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
  height: 36px;
  padding: 0.3rem 0.6rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.85rem;
  color: #2f3640;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.app-dropdown__trigger:hover:not(:disabled) {
  border-color: #b9c2d0;
}

.app-dropdown__trigger:focus,
.app-dropdown--open .app-dropdown__trigger {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.app-dropdown--disabled .app-dropdown__trigger {
  background: #f3f4f6;
  color: #9ca3af;
  cursor: not-allowed;
}

.app-dropdown__value {
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.app-dropdown__value--placeholder {
  color: #9ca3af;
}

.app-dropdown__caret {
  color: #6b7280;
  font-size: 0.75rem;
  line-height: 1;
}

.app-dropdown__panel {
  position: fixed;
  z-index: 1080;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.app-dropdown__panel--up {
  box-shadow: 0 -8px 24px rgba(15, 23, 42, 0.12);
}

.app-dropdown__search {
  padding: 0.4rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
}

.app-dropdown__search-input {
  width: 100%;
  padding: 0.3rem 0.55rem;
  border: 1px solid #d8dde6;
  border-radius: 6px;
  font-size: 0.82rem;
  outline: none;
}

.app-dropdown__search-input:focus {
  border-color: #16a07a;
  box-shadow: 0 0 0 2px rgba(22, 160, 122, 0.15);
}

.app-dropdown__list {
  list-style: none;
  margin: 0;
  padding: 0.25rem 0;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.app-dropdown__option {
  padding: 0.45rem 0.75rem;
  font-size: 0.85rem;
  color: #1f2a44;
  cursor: pointer;
}

.app-dropdown__option.is-active {
  background: #eef6f3;
}

.app-dropdown__option.is-selected {
  background: #16a07a;
  color: #ffffff;
}

.app-dropdown__option.is-selected.is-active {
  background: #138a69;
}

.app-dropdown__empty {
  padding: 0.6rem 0.75rem;
  font-size: 0.82rem;
  color: #6b7280;
  text-align: center;
}
</style>
